import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { FileSpreadsheet, Check, Building, Loader2, Upload, AlertTriangle } from 'lucide-react';
import { adminImportApi } from '../../../api/adminImportApi';
import { useError, ERROR_TYPES } from '../../../context/ErrorContext';

interface ImportRow {
  name: string;
  phone: string;
  countryCode: string;
  points: number;
  createdAt: Date;
  birthDay: number | null;
  birthMonth: number | null;
  lastVisit: Date | null;
}

interface Business {
  id: string;
  name: string;
}

// --- CONSTANTS & HELPERS (Extracted logic) ---

const US_AREA_CODES = [
  '619', '628', '415', '510', '323', '213', '408', '669', '707', '310',
  '424', '747', '805', '916', '925', '949', '951', '209', '530', '559',
  '626', '650', '657', '661', '760', '818', '831', '858', '909', '562'
];

const parseDate = (dateStr: string): Date | null => {
  if (!dateStr || dateStr.toLowerCase() === 'n/a') return null;
  const parts = dateStr.trim().split('-');
  if (parts.length !== 3) return null;
  const [day, month, year] = parts.map(part => parseInt(part.trim()));
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  if (year < 1000 || year > 3000) return null;
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime()) || date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
     return null;
  }
  return date;
};

const parsePhoneNumber = (phone: string): { phoneNumber: string; countryCode: string } => {
  let cleanNumber = phone.replace(/\D/g, '');
  let isUSNumber = false;
  if (cleanNumber.startsWith('1')) {
    const potentialUSNumber = cleanNumber.substring(1);
    if (potentialUSNumber.length >= 10) {
        if (US_AREA_CODES.some(code => potentialUSNumber.startsWith(code)) || potentialUSNumber.length === 10) {
          isUSNumber = true;
          cleanNumber = potentialUSNumber;
        }
    }
  }
  else if (cleanNumber.length >= 10 && US_AREA_CODES.some(code => cleanNumber.startsWith(code))) {
    isUSNumber = true;
  }
  if (!isUSNumber && cleanNumber.startsWith('52')) {
    const potentialMXNumber = cleanNumber.substring(2);
     if (potentialMXNumber.length >= 10) {
         cleanNumber = potentialMXNumber;
     }
  }
  if (cleanNumber.length < 10) {
    throw new Error('Número de teléfono inválido: debe tener al menos 10 dígitos después de limpiar prefijos');
  }
  cleanNumber = cleanNumber.slice(-10);
  return {
    phoneNumber: cleanNumber,
    countryCode: isUSNumber ? '+1' : '+52'
  };
};

const parseBirthDate = (birthStr: string): { day: number | null; month: number | null } => {
  if (!birthStr || birthStr.toLowerCase() === 'n/a') {
    return { day: null, month: null };
  }
  const parts = birthStr.trim().split('-');
  if (parts.length === 2) {
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    if (!isNaN(day) && !isNaN(month) && day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return { day, month };
    }
  }
  return { day: null, month: null };
};

// --- COMPONENT ---

const ImportUsersPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { addError, handleFirebaseError, clearErrors, addValidationError } = useError();

  const [selectedBusiness, setSelectedBusiness] = useState<string>('');
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [importedRows, setImportedRows] = useState<ImportRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [rawData, setRawData] = useState('');
  const [skippedCount, setSkippedCount] = useState(0);

  // Fetch available businesses
  useEffect(() => {
    const fetchBusinesses = async () => {
      // ✅ PROTECCIÓN DE RUTA MANUAL (Aunque debería estar en el Router)
      if (!currentUser || currentUser.role !== 'admin') return;
      
      setLoading(true);
      try {
        if (clearErrors) clearErrors();

        const list = await adminImportApi.listBusinesses();
        const businessesList = list.map(b => ({
          id: b.id,
          name: b.name || 'Negocio sin nombre'
        }));
        setBusinesses(businessesList);
      } catch (error) {
        console.error('Error fetching businesses:', error);
        if (handleFirebaseError) handleFirebaseError(error, 'fetch-businesses');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinesses();
  }, [currentUser, clearErrors, handleFirebaseError]);

  const processData = () => {
    setProcessing(true);
    setSuccess(null);
    setImportedRows([]);
    if (clearErrors) clearErrors();
    setSkippedCount(0);
    let currentSkipped = 0;

    try {
      const lines = rawData.split('\n').filter(line => line.trim());
      const linesToProcess = lines.slice(0, 500);

      if (linesToProcess.length === 0) {
           if (addValidationError) addValidationError('No hay datos para procesar.', 'import-data-empty');
           setProcessing(false);
           return;
      }

      const validationErrors: string[] = [];

      const processedRows: ImportRow[] = linesToProcess
        .map((line, index) => {
          const lineNumber = index + 1;
          try {
            const columns = line.split('\t').map(col => col.trim());

            if (columns.length < 9) {
               throw new Error(`Línea ${lineNumber}: Formato incorrecto, se esperan al menos 9 columnas.`);
            }

            const name = columns[0];
            const rawPhone = columns[1] || '';

            if (!rawPhone || rawPhone.toLowerCase() === 'n/a') {
                currentSkipped++;
                return null;
            }

            let phoneData;
            try {
              phoneData = parsePhoneNumber(rawPhone);
            } catch (error: any) {
              throw new Error(`Línea ${lineNumber} (Tel: ${rawPhone}): ${error.message}`);
            }

            const pointsStr = columns[5] || '0';
            // ✅ CORRECCIÓN: Usar parseFloat para soportar decimales (Cashback)
            const points = parseFloat(pointsStr);
             if (isNaN(points)) {
                 throw new Error(`Línea ${lineNumber}: Valor de Puntos/Saldo inválido ('${pointsStr}').`);
            }

            const createdAtStr = columns[6] || '';
            const createdAt = parseDate(createdAtStr);
             if (!createdAt) {
                 throw new Error(`Línea ${lineNumber}: Fecha de Registro inválida o faltante ('${createdAtStr}'). Formato DD-MM-YYYY.`);
             }

            if (!name) throw new Error(`Línea ${lineNumber}: Nombre es requerido.`);

            const birthDateStr = columns[7] || '';
            const { day: birthDay, month: birthMonth } = parseBirthDate(birthDateStr);

            const lastVisitStr = columns[8] || '';
            const lastVisit = parseDate(lastVisitStr);
            if(lastVisitStr && lastVisitStr.toLowerCase() !== 'n/a' && !lastVisit){
                 console.warn(`Línea ${lineNumber}: Formato de Última Visita ('${lastVisitStr}') no reconocido, se guardará como Nulo.`);
             }

            return {
              name, phone: phoneData.phoneNumber, countryCode: phoneData.countryCode,
              points, createdAt, birthDay, birthMonth, lastVisit
            };
          } catch (error: any) {
            console.warn('Error processing line:', line, error);
            validationErrors.push(error.message || `Error desconocido en línea ${lineNumber}`);
            return null;
          }
        })
        .filter((row): row is ImportRow => row !== null);

      setSkippedCount(currentSkipped);
      setImportedRows(processedRows);

      let feedbackMessage = `Procesamiento completado. Filas válidas: ${processedRows.length}.`;
      if (currentSkipped > 0) {
          feedbackMessage += ` Filas omitidas (teléfono N/A o vacío): ${currentSkipped}.`;
      }
      if (validationErrors.length > 0) {
          const errorSummary = `Errores encontrados: ${validationErrors.length}. Primeros errores: ${validationErrors.slice(0, 3).join('; ')}${validationErrors.length > 3 ? '...' : ''}`;
          feedbackMessage += ` ${errorSummary}`;
          // addValidationError solo acepta (mensaje, contexto): el detalle técnico se perdía.
          // addError sí lo guarda, y es la misma categoría (VALIDATION).
          if (addError) addError(feedbackMessage, ERROR_TYPES.VALIDATION, 'import-data-process', validationErrors);
      } else {
          if (processedRows.length > 0 || currentSkipped > 0) {
              setSuccess(feedbackMessage);
          }
      }

      if (processedRows.length === 0 && linesToProcess.length > 0) {
          if (currentSkipped === linesToProcess.length) {
              if (addValidationError) addValidationError('Todas las filas procesadas fueron omitidas por tener teléfono N/A o vacío.', 'import-data-all-skipped');
          } else if (validationErrors.length > 0) {
              // El error de validación ya se mostró
          } else {
              if (addValidationError) addValidationError('No se encontraron datos válidos para importar. Verifica el formato y los datos.', 'import-data-valid');
          }
      }

    } catch (error: any) {
      console.error('Error processing data:', error);
      if (addError) addError(`Error general al procesar los datos: ${error.message}`, 'processing', 'import-data-general');
    } finally {
       setProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!selectedBusiness) {
        if (addValidationError) addValidationError('Selecciona un negocio para importar los usuarios.', 'import-execute-business');
        return;
    }
     if (importedRows.length === 0) {
        if (addValidationError) addValidationError('No hay usuarios válidos procesados para importar. Procesa los datos primero.', 'import-execute-rows');
        return;
    }
     if (importing) return;

    setImporting(true);
    if (clearErrors) clearErrors();
    setSuccess(null);

    try {
      // UNA llamada bulk a lealify-base (reemplaza el bucle de addDoc a Firestore).
      const result = await adminImportApi.importClients(selectedBusiness, importedRows);

      if (result.errors && result.errors.length > 0) {
        const detail = result.errors.slice(0, 10).map(e => `Fila ${e.row}: ${e.reason}`);
        // handleFirebaseError DESCARTA el mensaje si el error no trae `.code` (y este es un
        // Error pelón): el usuario veía «Ha ocurrido un error inesperado.» en vez del recuento.
        // Con addError se conserva tanto el mensaje como el detalle por fila.
        if (addError) addError(`Se produjeron ${result.errors.length} errores durante la importación.`, ERROR_TYPES.SERVER, 'import-execute', detail);
      }

      let finalMessage = `Importación finalizada. ${result.imported} ${result.imported === 1 ? 'registro importado' : 'registros importados'} correctamente`;
      if (result.skipped > 0) {
        finalMessage += `. ${result.skipped} ${result.skipped === 1 ? 'omitido (ya existía)' : 'omitidos (ya existían)'}`;
      }
      if (result.errors && result.errors.length > 0) {
        finalMessage += `. Fallaron ${result.errors.length} ${result.errors.length === 1 ? 'registro' : 'registros'}`;
      }
      finalMessage += '.';
      setSuccess(finalMessage);

      setImportedRows([]);
      setRawData('');
      setSkippedCount(0);
    } catch (error: any) {
      console.error('Error importing:', error);
      if (handleFirebaseError) handleFirebaseError(error, 'import-execute');
    } finally {
      setImporting(false);
    }
  };

  const getImportButtonStatus = () => {
    if (processing) return 'Procesando datos...';
    if (!selectedBusiness) return 'Selecciona un negocio para continuar';
    if (importedRows.length === 0 && !processing) return 'Procesa datos válidos para poder importar';
    if (importing) return 'Importación en proceso...';
    return null;
  };

  // ✅ Protección de UI
  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 text-center p-4">
            <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-content">Acceso Restringido</h2>
            <p className="text-content-2 mt-2">No tienes permisos de administrador para ver esta página.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full space-y-6">
        <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-2xl font-bold text-content flex items-center">
            <FileSpreadsheet className="h-6 w-6 mr-2" style={{ color: 'rgb(var(--c-accent))' }} />
            Importar Usuarios
          </h1>
          <p className="mt-1 text-sm text-content-2">
            Copia y pega los datos de usuarios (máximo <span className='font-semibold'>500</span> registros). Las filas con teléfono N/A o vacío serán omitidas.
          </p>
        </div>

         {loading && (
            <div className="flex justify-center items-center p-4">
                <Loader2 className="h-6 w-6 animate-spin text-content-3" />
                <span className="ml-2 text-content-2">Cargando negocios...</span>
            </div>
         )}

        {success && (
          <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-300">{success}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-surface shadow-md rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-6">
              {!loading && businesses.length > 0 && (
                 <div className="animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                 <label htmlFor="business" className="block text-sm font-medium text-content-2">
                   Seleccionar Negocio
                 </label>
                 <div className="mt-1 flex items-center">
                   <Building className="h-5 w-5 text-content-3 mr-2 flex-shrink-0" />
                   <select
                     id="business"
                     value={selectedBusiness}
                     onChange={(e) => setSelectedBusiness(e.target.value)}
                     className="block w-full rounded-md border-line shadow-sm focus:ring-accent focus:border-accent sm:text-sm"
                     style={{ borderColor: selectedBusiness ? 'rgb(var(--c-accent))' : undefined }}
                     disabled={importing || processing}
                   >
                     <option value="">Selecciona un negocio...</option>
                     {businesses.map((business) => (
                       <option key={business.id} value={business.id}>
                         {business.name}
                       </option>
                     ))}
                   </select>
                 </div>
               </div>
              )}
               {!loading && businesses.length === 0 && currentUser?.role === 'admin' && (
                 <div className="text-center text-orange-600 dark:text-orange-400 p-4 border border-orange-300 rounded">
                   No se encontraron negocios.
                 </div>
               )}

              <div className="animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                <label htmlFor="data" className="block text-sm font-medium text-content-2">
                  Datos de Usuarios a Importar
                </label>
                <p className="mt-1 text-sm text-content-3">
                  Pega aquí los datos desde Excel/Sheets.
                  <br />
                  <span className="font-semibold">Formato columnas (separadas por tab):</span><br />
                   1. Nombre, 2. Teléfono, 3. Email (N/A), 4. Código QR (ignorar),
                   5. Sucursal (ignorar), <span className="font-bold">6. Puntos/Saldo</span>, 7. Fecha Registro (DD-MM-YYYY), 8. Fecha Nacimiento (DD-MM o N/A), 9. Última Visita (DD-MM-YYYY o N/A)
                </p>
                <div className="mt-1">
                  <textarea
                    id="data"
                    rows={12}
                    className="shadow-sm block w-full sm:text-sm border-line rounded-md font-mono focus:ring-accent focus:border-accent"
                    style={{ borderColor: rawData ? 'rgb(var(--c-accent))' : undefined }}
                    placeholder="Ejemplo: Aleph	526863312235	N/A	294522010le	Snowtea Toscana	9.50	01-11-2024	22-06	01-11-2024"
                    value={rawData}
                    onChange={(e) => setRawData(e.target.value)}
                     disabled={importing || processing}
                  />
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={processData}
                     disabled={!rawData || importing || processing || !selectedBusiness}
                    className="inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm leading-4 font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                      {processing ? (
                          <>
                            <Loader2 className="animate-spin h-4 w-4 mr-2" />
                            Procesando...
                          </>
                      ) : "Procesar Datos"}
                  </button>
                   {skippedCount > 0 && !processing && (
                        <span className="ml-3 text-xs text-content-3 italic">
                             ({skippedCount} {skippedCount === 1 ? 'fila omitida' : 'filas omitidas'} por teléfono N/A)
                        </span>
                   )}
                </div>
              </div>

              {importedRows.length > 0 && (
                <div className="animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                  <h3 className="text-lg font-medium text-content mb-4">
                    Vista Previa ({importedRows.length} registros válidos)
                  </h3>
                  <div className="overflow-x-auto border rounded-md">
                    <table className="min-w-full divide-y divide-line">
                      <thead className="bg-surface-2">
                        <tr>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Nombre</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Teléfono</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Saldo/Puntos</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Cumpleaños</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Registro</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Última Visita</th>
                        </tr>
                      </thead>
                      <tbody className="bg-surface divide-y divide-line">
                        {importedRows.slice(0, 10).map((row, index) => (
                          <tr key={index} className="animate-fade-in-up" style={{ animationDelay: `${0.4 + index * 0.02}s` }}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-content">{row.name}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-content-3">{row.countryCode}{row.phone}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-content-3 text-center">
                                {/* ✅ Muestra el valor decimal si existe */}
                                {row.points % 1 !== 0 ? `$${row.points.toFixed(2)}` : row.points}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-content-3 text-center">{row.birthDay && row.birthMonth ? `${String(row.birthDay).padStart(2, '0')}-${String(row.birthMonth).padStart(2, '0')}` : 'N/A'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-content-3">{row.createdAt.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-content-3">{row.lastVisit ? row.lastVisit.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}</td>
                          </tr>
                        ))}
                         {importedRows.length > 10 && (
                             <tr>
                                   <td colSpan={6} className="text-center text-xs text-content-3 py-2">
                                            ... y {importedRows.length - 10} más registros procesados.
                                   </td>
                             </tr>
                         )}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 flex flex-col sm:flex-row justify-end items-center space-y-2 sm:space-y-0 sm:space-x-3 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                       <div className="text-sm text-content-3 italic text-right flex-grow">
                          {getImportButtonStatus()}&nbsp;
                       </div>
                       <button
                           type="button"
                           onClick={handleImport}
                           disabled={importing || processing || !selectedBusiness || importedRows.length === 0}
                           className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent w-full sm:w-auto"
                           style={{ backgroundColor: 'rgb(var(--c-accent))' }}
                           title={getImportButtonStatus() || `Importar ${importedRows.length} usuarios`}
                          >
                           {importing ? (
                             <><Loader2 className="animate-spin h-4 w-4 mr-2" /> Importando...</>
                           ) : (
                             <><Upload className="h-4 w-4 mr-2" /> Importar {importedRows.length} {importedRows.length === 1 ? 'Usuario' : 'Usuarios'}</>
                           )}
                          </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ImportUsersPage;